/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import Breadcrumbs from '@Components/Breadcrumbs';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ShowFor from '@Components/ShowFor';
import { Venue } from '@Types/venue';
import { buildFilterParams } from '@Utils/utils';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVenues } from '../../hooks/useVenues';
import SettingDisplay from '@Components/SettingDisplay';
import { Store } from 'lucide-react';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';

const venueColumnConfigs: ColumnConfig<Venue>[] = [
  {
    id: 'name',
    headerLabel: 'Venue Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'disabledAt',
    headerLabel: 'Status',
    cell: (row) => {
      const rowData = row.row.original;
      return rowData.disabledAt ? (
        <Badge variant="destructive">Disabled</Badge>
      ) : (
        <Badge variant="outline">Enabled</Badge>
      );
    },
    type: 'text',
    customSortingFn: (rowA, rowB) => {
      const a = rowA.original.disabledAt ? 1 : 0;
      const b = rowB.original.disabledAt ? 1 : 0;
      return a - b;
    },
  },
  {
    id: 'spaces',
    headerLabel: 'Number of Spaces',
    accessor: (row) => row.spaces?.length ?? 0,
    type: 'number',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'toastSettings',
    header: () => (
      <div className="flex items-center gap-1">
        <img src="/toast-favicon.png" className="w-4" />
        <span>Toast Settings</span>
      </div>
    ),
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex flex-wrap space-x-3">
          <SettingDisplay id={row.toastRestaurantGuid} label="Restaurant" />
          <SettingDisplay
            id={row.toastPrivateDiningMenuGuid}
            label="Private Dining Menu"
          />
          <SettingDisplay
            id={row.toastCorporateDiningMenuGuid}
            label="Corporate Dining Menu"
          />
          <SettingDisplay
            id={row.toastTicketedEventMenuGuid}
            label="Ticketed Event Menu"
          />
          <SettingDisplay
            id={row.toastTicketedEventServiceChargeGuid}
            label="Ticketed Event Service Charge"
          />
        </div>
      );
    },
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
  {
    id: 'tripleSeatSettings',
    header: () => (
      <div className="flex items-center gap-1">
        <img src="/triple-seat-favicon-400x400.png" className="w-4" />
        <span>Triple Seat Settings</span>
      </div>
    ),
    cell: (info) => {
      const row = info.row.original;
      return <SettingDisplay id={row.tripleSeatId} label="Location" />;
    },
    type: 'text',
    enableSorting: false,
    enableFiltering: false,
  },
];

export default function Venues() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Venue>({
    defaultSort: {
      id: 'name',
    },
    columnsConfig: venueColumnConfigs,
    baseSearchParams: () => ({
      include: 'spaces',
    }),
    buildFilterParams: (filters) => buildFilterParams(filters),
    quickFilterFields: [{ field: 'name', label: 'Venue Name' }],
    initialPageSize: 10,
  });

  const { data: payload, status } = useVenues(controller.searchParams);
  const { data: venuesMeta, status: venuesMetaStatus } = useVenues();

  const rows: Venue[] = Array.isArray(payload) ? payload : [];

  const meta = useMemo(() => {
    if (venuesMetaStatus !== 'success') return undefined;
    if (!Array.isArray(venuesMeta)) return undefined;
    return {
      total: Array.isArray(venuesMeta) ? venuesMeta.length : 0,
    };
  }, [venuesMeta, venuesMetaStatus]);

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Locations', href: '/locations' },
          { name: 'Venues', href: '/venues' },
        ]}
      />

      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Venues</Heading>
          <p>Manage your venues.</p>
        </div>

        <div className="flex justify-end">
          <ShowFor permissions={['VENUES_WRITE']}>
            <Button asChild>
              <Link to="/venues/create">Create a venue</Link>
            </Button>
          </ShowFor>
        </div>
      </div>
      <Card>
        <CardContent>
          <ResourceTableView<Venue>
            label="Venues"
            controller={controller}
            data={rows}
            meta={meta}
            status={status}
            summaryIcon={<Store className="h-4 w-4 text-zinc-500" />}
            summaryLabel="Total Venues: "
            onRowClick={(row) => navigate(`/venues/${row.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
